/**
 * Общая база квартир: что брали раньше и как было.
 *
 * Проверенные сверху, чёрный список — внизу и приглушён. Карточка открывает
 * подробности: отзывы сотрудников, отметку старших и историю броней.
 *
 * Два вида — плитки с фото и плотный список; выбор запоминается. Карточка
 * появляется сама на каждую подтверждённую бронь.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ban,
  BadgeCheck,
  BookmarkPlus,
  Building2,
  CalendarDays,
  LayoutGrid,
  Link2Off,
  List,
  MapPin,
  MessageSquareText,
  RotateCcw,
  Search,
} from 'lucide-react';
import type { StayBookingFacetsDto, StayPlatform, StayPropertyDto, StayPropertyStatus } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import { EmptyState, INPUT, PlatformMark, PropertyThumb, RatingBadge, StatusPill } from './bits.tsx';
import { formatDate, plural } from './meta.ts';
import { PropertyDialog } from './PropertyDialog.tsx';
import { SavePropertyDialog } from './SavePropertyDialog.tsx';
import { useIsMobile } from '../../lib/useMedia.ts';

/** Выбранный фильтр подсвечен: видно, что выборка сужена. */
const SELECTED = 'border-brand bg-primary/5 text-heading';

const FILTERS: Array<{ key: StayPropertyStatus | 'ALL'; label: string; icon?: React.ReactNode }> = [
  { key: 'ALL', label: 'Все' },
  { key: 'VERIFIED', label: 'Проверенные', icon: <BadgeCheck className="size-3.5" /> },
  { key: 'NEUTRAL', label: 'Без отметки' },
  { key: 'BLACKLISTED', label: 'Чёрный список', icon: <Ban className="size-3.5" /> },
];

interface Props {
  onBook: (property: StayPropertyDto) => void;
  onOpenUrl?: (url: string) => void;
}

type View = 'grid' | 'list';
const VIEW_KEY = 'tw.stays.propertyView';

function rememberedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export function PropertiesPanel({ onBook, onOpenUrl }: Props) {
  const { api, can } = useSession();
  const [items, setItems] = useState<StayPropertyDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StayPropertyStatus | 'ALL'>('ALL');
  const [platform, setPlatform] = useState<StayPlatform | ''>('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [facets, setFacets] = useState<StayBookingFacetsDto>({ countries: [], cities: [] });
  const [view, setViewState] = useState<View>(rememberedView);
  const mobile = useIsMobile();
  /** Телефон: площадка, страна и город — за кнопкой «Фильтры». */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.stayProperties({
        ...(query ? { q: query } : {}),
        ...(status !== 'ALL' ? { status } : {}),
        ...(platform ? { platform } : {}),
        ...(country ? { country } : {}),
        ...(city ? { city } : {}),
      });
      setItems(res.items);
    } catch (err) {
      setError(describeError(err));
    }
    api.stayPropertyFacets().then(setFacets).catch(() => undefined);
  }, [api, query, status, platform, country, city]);

  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // без хранилища выбор просто не запомнится
    }
  };

  const cityOptions = useMemo(
    () => facets.cities.filter((c) => !country || c.country === country || c.country === null),
    [facets, country],
  );
  const filtered = !!(query || status !== 'ALL' || platform || country || city);
  const reset = () => {
    setQ('');
    setStatus('ALL');
    setPlatform('');
    setCountry('');
    setCity('');
  };

  const bookedLabel = (p: StayPropertyDto) =>
    p.bookingCount > 0
      ? `${p.bookingCount} ${plural(p.bookingCount, 'бронь', 'брони', 'броней')} · ${formatDate(p.lastBookedAt!)}`
      : 'не бронировали';

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className={cn('flex gap-1.5', mobile ? '-mx-3 w-[calc(100%+1.5rem)] flex-nowrap overflow-x-auto px-3 pb-0.5' : 'flex-wrap')}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              className={cn(
                'inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                status === f.key
                  ? 'border-transparent bg-primary text-primary-foreground shadow-[0_2px_10px_-4px_var(--accent-fill)]'
                  : 'border-border bg-card text-soft hover:border-brand hover:text-brand',
              )}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input className={cn(INPUT, 'pl-9')} placeholder="Название, город, адрес" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {mobile && (
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className={cn(
              'inline-flex h-9 flex-none items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium',
              platform || country || city || filtersOpen ? 'border-brand bg-primary/5 text-brand' : 'border-border bg-card text-soft',
            )}
          >
            Фильтры
          </button>
        )}
        {(!mobile || filtersOpen) && (
        <>
        <select className={cn(INPUT, 'w-auto pr-8', platform && SELECTED)} value={platform} onChange={(e) => setPlatform(e.target.value as StayPlatform | '')}>
          <option value="">Airbnb и Booking</option>
          <option value="AIRBNB">Airbnb</option>
          <option value="BOOKING">Booking</option>
        </select>
        <select
          className={cn(INPUT, 'w-auto max-w-[200px] pr-8', country && SELECTED)}
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
            setCity('');
          }}
        >
          <option value="">Все страны</option>
          {facets.countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select className={cn(INPUT, 'w-auto max-w-[200px] pr-8', city && SELECTED)} value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Все города</option>
          {cityOptions.map((c) => (
            <option key={c.city} value={c.city}>
              {c.city}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex h-8 items-center gap-1 rounded-full border-0 bg-transparent px-2.5 text-[12px] font-medium text-brand hover:bg-secondary"
          >
            <RotateCcw className="size-3.5" /> Сбросить
          </button>
        )}
        </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-full bg-secondary p-1" role="group" aria-label="Вид">
            {(
              [
                { key: 'grid', label: 'Плитки', icon: <LayoutGrid className="size-4" /> },
                { key: 'list', label: 'Список', icon: <List className="size-4" /> },
              ] as const
            ).map((v) => (
              <button
                key={v.key}
                type="button"
                title={v.label}
                aria-label={v.label}
                aria-pressed={view === v.key}
                onClick={() => setView(v.key)}
                className={cn(
                  'grid size-7 place-items-center rounded-full border-0 p-0 transition-colors',
                  view === v.key ? 'bg-card text-heading shadow-sm' : 'bg-transparent text-muted-foreground hover:text-heading',
                )}
              >
                {v.icon}
              </button>
            ))}
          </div>
          {can('stays.book') && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAdding(true)}>
              <BookmarkPlus className="size-4" /> Квартира
            </Button>
          )}
        </div>
      </div>

      {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {items && items.length === 0 ? (
        <EmptyState icon={<Building2 className="size-6" />} title={filtered ? 'Ничего не нашлось' : 'База пока пуста'}>
          Квартира попадает сюда сама с каждой подтверждённой бронью, а ещё — кнопкой «В базу» со страницы во вкладке «Браузер». Оценки и
          комментарии сотрудников копятся здесь же — по ним видно, что брать снова, а что нет.
        </EmptyState>
      ) : mobile && view === 'list' ? (
        /* Телефон: «список» — плотные строки; таблица в 820 px сюда не входит. */
        <div className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          {(items ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setOpenId(p.id)}
              className={cn(
                'flex items-center gap-3 rounded-none border-0 bg-transparent px-3 py-2.5 text-left',
                p.status === 'BLACKLISTED' && 'opacity-70',
              )}
            >
              <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className="size-11 flex-none rounded-lg" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-medium text-heading">{p.title}</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <span className="truncate">{[p.city, p.country].filter(Boolean).join(', ') || 'город не указан'}</span>
                  {p.rating != null && <RatingBadge rating={p.rating} />}
                </span>
              </span>
              <StatusPill status={p.status} />
            </button>
          ))}
        </div>
      ) : view === 'list' ? (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <table className="w-full min-w-[820px] border-separate border-spacing-0 text-left text-[13px]">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <th className="border-b border-border px-4 py-2.5">Квартира</th>
                <th className="border-b border-border px-3 py-2.5">Город</th>
                <th className="border-b border-border px-3 py-2.5">Статус</th>
                <th className="border-b border-border px-3 py-2.5">Оценка</th>
                <th className="border-b border-border px-3 py-2.5">Брони</th>
                <th className="border-b border-border px-3 py-2.5 text-right">Отзывы</th>
              </tr>
            </thead>
            <tbody>
              {(items ?? []).map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setOpenId(p.id)}
                  className={cn('cursor-pointer transition-colors hover:bg-accent/60', p.status === 'BLACKLISTED' && 'opacity-70 hover:opacity-100')}
                >
                  <td className="border-b border-border/60 px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className="size-10 flex-none rounded-lg" />
                      <div className="min-w-0">
                        <p className="max-w-[340px] truncate font-medium text-heading">{p.title}</p>
                        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <PlatformMark platform={p.platform} size="sm" />
                          {!p.url && (
                            <span className="inline-flex items-center gap-1" title="Ссылки на объявление нет — привяжется, когда квартиру откроют в браузере">
                              <Link2Off className="size-3" /> без ссылки
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="border-b border-border/60 px-3 py-2.5 text-soft">{[p.city, p.country].filter(Boolean).join(', ') || '—'}</td>
                  <td className="border-b border-border/60 px-3 py-2.5">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="border-b border-border/60 px-3 py-2.5">
                    <RatingBadge rating={p.rating} count={p.ratingCount} />
                  </td>
                  <td className="border-b border-border/60 px-3 py-2.5 text-soft">{bookedLabel(p)}</td>
                  <td className="border-b border-border/60 px-3 py-2.5 text-right tabular-nums text-soft">{p.commentCount || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {(items ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setOpenId(p.id)}
              className={cn(
                'group flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-0 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:bg-card hover:shadow-lg',
                p.status === 'BLACKLISTED' && 'opacity-70 hover:opacity-100',
              )}
            >
              <div className="relative">
                <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className={p.photoUrl ? 'aspect-[16/9] w-full' : 'h-24 w-full'} />
                <span className="absolute left-2.5 top-2.5">
                  <PlatformMark platform={p.platform} />
                </span>
                {p.status !== 'NEUTRAL' && (
                  <span className="absolute right-2.5 top-2.5 rounded-full bg-card/90 backdrop-blur">
                    <StatusPill status={p.status} />
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-1.5 p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="line-clamp-2 text-[14px] font-semibold leading-snug text-heading">{p.title}</p>
                  <RatingBadge rating={p.rating} />
                </div>
                <p className="flex items-center gap-1 text-[12px] text-muted-foreground">
                  <MapPin className="size-3.5" />
                  {[p.city, p.country].filter(Boolean).join(', ') || 'город не указан'}
                </p>
                <div className="mt-auto flex items-center gap-3 pt-2 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="size-3.5" />
                    {bookedLabel(p)}
                  </span>
                  {p.commentCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <MessageSquareText className="size-3.5" />
                      {p.commentCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <PropertyDialog
        propertyId={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => void load()}
        onBook={(p) => {
          setOpenId(null);
          onBook(p);
        }}
        onOpenUrl={onOpenUrl}
      />
      <SavePropertyDialog
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={(p) => {
          setAdding(false);
          void load();
          setOpenId(p.id);
        }}
      />
    </div>
  );
}
